import { useEffect, useState, useRef } from "react";
import { FireServices } from "../../../../../shared/services/api/fire/FireServices";
import { normalizeCoverage, NormalizedCoverage } from "../../utils/normalizeCoverage";
import { globalRemoveMaskReal2 } from "../../../../../shared/utils";
import { calculateCoveragesValues } from "../../utils/calculateCoveragesValues";

interface UseUnifiedCoveragesParams {
    mode: "create" | "edit";
    fireQuoteData: any | null;
    clientId: string | null;
    companyId: string;
    propertyType: string;
    rentValue: string;
}

export function useUnifiedCoverages({
    mode,
    fireQuoteData,
    clientId,
    companyId,
    propertyType,
    rentValue,
}: UseUnifiedCoveragesParams) {
    const [coverages, _setCoverages] = useState<NormalizedCoverage[]>([]);
    const [loading, setLoading] = useState(false);
    const loadedRef = useRef(false);

    // 1. Load Logic (Handles both Create and Edit)
    useEffect(() => {
        if (loadedRef.current) return;
        if (!clientId || !companyId || !propertyType) return;

        // Se for EDIT, precisamos esperar o fireQuoteData carregar também
        if (mode === "edit" && !fireQuoteData) return;

        const fetchCatalogAndMerge = async () => {
            setLoading(true);
            try {
                // Busca o catálogo completo (metadata)
                const result = await FireServices.getAllCoverageByOccupation(
                    clientId,
                    companyId,
                    propertyType
                );

                let finalCoverages: NormalizedCoverage[] = [];

                if (result && !((result as any) instanceof Error)) {
                    const catalog = (result as any[]).map((c: any) => normalizeCoverage(c));

                    if (mode === "edit" && fireQuoteData?.coverages) {
                        // MERGE: Catálogo + Dados Salvos
                        const savedMap = new Map<string, NormalizedCoverage>();
                        fireQuoteData.coverages.forEach((c: any) => {
                            const norm = normalizeCoverage(c);
                            savedMap.set(norm.id, norm);
                        });

                        finalCoverages = catalog.map((catItem) => {
                            const savedItem = savedMap.get(catItem.id);
                            if (savedItem) {
                                return {
                                    ...savedItem,
                                    enabled: true, // Estava salvo, então está ativo
                                    source: "load",
                                    limite_min: savedItem.limite_min ?? catItem.limite_min,
                                    limite_max: savedItem.limite_max ?? catItem.limite_max,
                                    name: savedItem.name || catItem.name,
                                };
                            } else {
                                return {
                                    ...catItem,
                                    enabled: false, // Não estava salvo, então desativado
                                    source: "load",
                                };
                            }
                        });
                    } else {
                        // CREATE MODE: Calcular valores iniciais com base no aluguel e fator
                        // Usando o utilitário refatorado
                        finalCoverages = calculateCoveragesValues({
                            coverages: catalog as any[],
                            rentValue,
                            quotationId: "novo"
                        }) as unknown as NormalizedCoverage[];
                    }

                    _setCoverages(finalCoverages);
                    loadedRef.current = true;
                }
            } catch (error) {
                console.error("Error fetching coverage catalog", error);
            } finally {
                setLoading(false);
            }
        };

        fetchCatalogAndMerge();
    }, [mode, clientId, companyId, propertyType, fireQuoteData]);

    // Wrapper para detectar alterações manuais
    const setCoverages = (action: React.SetStateAction<NormalizedCoverage[]>) => {
        _setCoverages(prev => {
            const next = typeof action === 'function' ? action(prev) : action;

            return next.map(n => {
                const old = prev.find(p => p.id === n.id);
                if (!old) return n;

                // 🆕 Se estiver habilitando e o valor for 0, calcula automático (Aluguel * Fator)
                if (!old.enabled && n.enabled && (!n.coverage_value || n.coverage_value === 0)) {
                    const rent = Number(globalRemoveMaskReal2(rentValue ?? "0"));
                    const factor = Number(n.factor ?? 1);
                    const min = Number(n.limite_min ?? 0);
                    const max = Number(n.limite_max ?? 0);

                    let val = rent * factor;
                    if (min > 0 && val < min) val = min;
                    if (max > 0 && val > max) val = max;

                    return { ...n, coverage_value: val };
                }

                if (n.coverage_value !== old.coverage_value) {
                    return { ...n, is_changed_by_user: true };
                }

                if (old.is_changed_by_user) {
                    return { ...n, is_changed_by_user: true };
                }

                return n;
            });
        });
    };

    // 2. Recalcular valores das coberturas quando o aluguel mudar (apenas CREATE mode)
    useEffect(() => {
        if (mode !== "create") return;
        if (!loadedRef.current) return;
        if (coverages.length === 0) return;

        const rent = Number(globalRemoveMaskReal2(rentValue ?? "0"));
        if (rent === 0) return;

        // Usando o utilitário refatorado para recalcular
        const recalculated = calculateCoveragesValues({
            coverages: coverages as any[],
            rentValue,
            quotationId: "novo"
        }) as unknown as NormalizedCoverage[];

        _setCoverages(recalculated);

    }, [mode, rentValue, coverages.length]);

    return {
        coverages,
        setCoverages, // Expose setter so calculation hook or UI can update values
        loading,
    };
}
